import { AxiosResponse } from "axios";
import { NavigateFunction } from "react-router-dom";
import { OptionsObject, SnackbarKey, SnackbarMessage } from "notistack";
import { BinanceAuthData } from "../../../../features/authentication/hooks/use-auth/types/BinanceAuthData";
import { UserAuthData } from "../../../../features/authentication/hooks/use-auth/types/UserAuthData";
import { httpClient } from "../../../shared/services/httpClient";
import { HttpMethod } from "../../../shared/types/HttpMethod";
import { errorMessages } from "../../../shared/constants/error.constants";
import { ErrorCode } from "../../../shared/types/ErrorCode";

type CreateBinanceOptionsRequestServiceParameters = {
    userAuthData: UserAuthData | null;
    isUserAuthenticated: () => boolean;
    binanceAuthData: BinanceAuthData;
    navigate: NavigateFunction;
    enqueueSnackbar: (message: SnackbarMessage, options?: OptionsObject) => SnackbarKey;
}

export function createBinanceOptionsRequestService({
    userAuthData,
    isUserAuthenticated,
    binanceAuthData,
    navigate,
    enqueueSnackbar,
}: CreateBinanceOptionsRequestServiceParameters) {
    const apiKeyHeaderName = "X-MBX-APIKEY";

    const hasBrowserSecrets = (): boolean =>
        binanceAuthData.apiKey.trim() !== "" &&
        binanceAuthData.secretKey.trim() !== "";

    const signAndSendRequest = <TDto, TModel>(
        httpMethod: HttpMethod,
        endpoint: string,
        queryParamsForSigning: Record<string, string>,
        extraQueryParams: Record<string, string> | null,
        getValidatedDto: (dto: any) => TDto | null,
        map: (dto: TDto) => TModel,
    ): Promise<TModel | null> => {
        const query = new URLSearchParams(queryParamsForSigning).toString();

        return sendAuthenticatedRequestWithSignedQuery(
            query,
            (jwtToken: string, timestamp: number, signature: string) => {
                const headers = {
                    Authorization: `Bearer ${jwtToken}`,
                    [apiKeyHeaderName]: `${binanceAuthData.apiKey}`,
                };

                const params = {
                    ...queryParamsForSigning,
                    timestamp,
                    signature,
                    ...(extraQueryParams ?? {})
                };

                switch (httpMethod) {
                    case HttpMethod.Get:
                        return httpClient.instance.get<TDto | null>(endpoint, { headers: headers, params: params });

                    case HttpMethod.Post:
                        return httpClient.instance.post<TDto | null>(endpoint, null, { headers: headers, params: params });

                    case HttpMethod.Delete:
                        return httpClient.instance.delete<TDto | null>(endpoint, { headers: headers, params: params });
                }
            },
            getValidatedDto,
            map);
    }

    const sendAuthenticatedRequestWithSignedQuery = async <TDto, TModel>(
        query: string,
        sendRequest: (jwtToken: string, timestamp: number, signature: string) => Promise<AxiosResponse<TDto | null, any>>,
        getValidatedDto: (dto: any) => TDto | null,
        map: (dto: TDto) => TModel,
    ): Promise<TModel | null> => {
        if (!isUserAuthenticated() || userAuthData === null || userAuthData.jwtToken === null) {
            navigate("/login");
            return null;
        }

        const timestamp = Date.now();
        const signature = await generateSignature(`${query}&timestamp=${timestamp}`, binanceAuthData.secretKey);

        try {
            const response = await sendRequest(userAuthData.jwtToken, timestamp, signature);
            return processResponse(response, getValidatedDto, map);
        } catch(error: any) {
            handleErrorsByCodes(error.response.data.split(", "), error.response.data);
            return null;
        }
    }

    const sendRequest = <TDto, TModel>(
        httpMethod: HttpMethod,
        endpoint: string,
        queryParams: Record<string, string | number | null | undefined> | null,
        getValidatedDto: (dto: any) => TDto | null,
        map: (dto: TDto) => TModel,
    ): Promise<TModel | null> =>
        sendAuthenticatedRequest(
            (jwtToken: string) => {
                const headers = {
                    Authorization: `Bearer ${jwtToken}`,
                };

                switch (httpMethod) {
                    case HttpMethod.Get:
                        return httpClient.instance.get<TDto | null>(endpoint, { headers: headers, params: queryParams });

                    case HttpMethod.Post:
                        return httpClient.instance.post<TDto | null>(endpoint, null, { headers: headers, params: queryParams });

                    case HttpMethod.Delete:
                        return httpClient.instance.delete<TDto | null>(endpoint, { headers: headers, params: queryParams });
                }
            },
            getValidatedDto,
            map);

    const sendAuthenticatedRequest = async <TDto, TModel>(
        sendRequest: (jwtToken: string) => Promise<AxiosResponse<TDto | null, any>>,
        getValidatedDto: (dto: any) => TDto | null,
        map: (dto: TDto) => TModel,
    ): Promise<TModel | null> => {
        if (!isUserAuthenticated() || userAuthData === null || userAuthData.jwtToken === null) {
            navigate("/login");
            return null;
        }

        try {
            const response = await sendRequest(userAuthData.jwtToken);
            return processResponse(response, getValidatedDto, map);
        } catch(error: any) {
            handleErrorsByCodes(error.response.data.split(", "), error.response.data);
            return null;
        }
    }

    const processResponse = <TDto, TModel>(
        response: AxiosResponse<TDto | null, any>,
        getValidatedDto: (dto: any) => TDto | null,
        map: (dto: TDto) => TModel,
    ): TModel | null => {
        const dto = getValidatedDto(response.data);

        if (dto === null) {
            enqueueSnackbar("Internal error: invalid input DTO", { variant: "error" });
            return null;
        }

        return map(dto);
    }

    const handleErrorsByCodes = (errorCodes: string[], unknownErrorMessage: string) => {
        const messages = new Set<string>();

        errorCodes.forEach((errorCode: string) => {
            const code = ErrorCode[errorCode as keyof typeof ErrorCode];

            if (code)
                messages.add(errorMessages[code]);
            else
                messages.add(`${errorMessages[0]}: ${unknownErrorMessage}`);
        });

        messages.forEach(message => enqueueSnackbar(message, { variant: "error" }));
    }

    async function generateSignature(message: string, secret: string): Promise<string> {
        const encoder = new TextEncoder();

        const key = await window.crypto.subtle.importKey(
            "raw",
            encoder.encode(secret),
            { name: "HMAC", hash: "SHA-256" },
            false,
            ["sign"]
        );

        const signatureBuffer = await window.crypto.subtle.sign(
            "HMAC", key, encoder.encode(message)
        );

        const byteArray = Array.from(new Uint8Array(signatureBuffer));
        return byteArray.map(b => b.toString(16).padStart(2, "0")).join("");
    }

    return {
        hasBrowserSecrets,
        signAndSendRequest,
        sendRequest,
    };
}
